import {Children, useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Link from "next/link";
import {Dropdown, useDropdownMenu} from "react-overlays";
import {useTranslation} from "next-i18next";
import {useRouter} from "next/router";
import Container from "./Container";
import LangBox from "./LangBox";
import Button from "./Button";
import {sendEvent, hrefToEventAction} from "../utils/analytics";
import IconArrowDown from "../public/images/arrow-down.svg";
import IconClose from "../public/images/close.svg";
import IconUser from "../public/images/user.svg";

export const itemsConvert = [
  {href: "/pdf-to-word", text: "nav.pdf-to-word"},
  {href: "/word-to-pdf", text: "nav.word-to-pdf"},
  {href: "/pdf-to-excel", text: "nav.pdf-to-excel"},
  {href: "/excel-to-pdf", text: "nav.excel-to-pdf"},
  {href: "/pdf-to-jpg", text: "nav.pdf-to-jpg"},
  {href: "/pdf-to-png", text: "nav.pdf-to-png"},
  {href: "/pdf-to-ppt", text: "nav.pdf-to-ppt"},
  // { href: '/html-to-pdf', text: 'nav.html-to-pdf' },
  // { href: '/jpg-to-pdf', text: 'nav.jpg-to-pdf' },
];

export const itemsEdit = [
  {href: "/", text: "nav.edit-pdf"},
  {href: "/?tool=sign", text: "nav.fill-sign"},
  {href: "/?tool=form", text: "nav.create-forms"},
  {href: "/rotate-pdf", text: "nav.rotate-pdf"},
  {href: "/split-pdf", text: "nav.split-pdf"},
  {href: "/compress-pdf", text: "nav.compress-pdf"},
  // { href: '/unlock-pdf', text: 'nav.unlock-pdf' },
];

export const itemsOther = [
  // { href: '/faq', text: 'nav.faq' },
  // { href: '/privacy-policy', text: 'nav.privacy-policy' },
  // { href: '/terms', text: 'nav.terms' },
  // { href: '/do-not-sell', text: 'nav.do-not-sell' },
  // {href: "/edit", text: "nav.edit"},
  // {href: "/fill-sign", text: "nav.fill-sign"},
];

export const itemsOtherRight = [
  // { href: '/faq', text: 'nav.faq' },
  // { href: '/privacy-policy', text: 'nav.privacy-policy' },
  // { href: '/terms', text: 'nav.terms' },
  // { href: '/do-not-sell', text: 'nav.do-not-sell' },
  {href: "/contact", text: "nav.contact"},
];

export const itemsOtherRightMain = [
  // { href: '/faq', text: 'nav.faq' },
  // { href: '/privacy-policy', text: 'nav.privacy-policy' },
  // { href: '/terms', text: 'nav.terms' },
  // { href: '/do-not-sell', text: 'nav.do-not-sell' },
  {href: "?#howto", text: "nav.howto"},
  {href: "/contact", text: "nav.contact"},
];

const headerItems = [
  // {
  //   title: "Edit & Sign",
  //   items: [
  //     {href: "/edit", text: "nav.edit"},
  //     {href: "/fill-sign", text: "nav.fill-sign"},
  //     {href: "/create-forms", text: "nav.create-forms"},
  //     {href: "/delete-pages", text: "nav.delete-pages"},
  //   ],
  // },
  {
    title: "Convert from PDF",
    items: [
      {href: "/pdf-to-word", text: "nav.pdf-to-word"},
      {href: "/pdf-to-excel", text: "nav.pdf-to-excel"},
      {href: "/pdf-to-jpg", text: "nav.pdf-to-jpg"},
      {href: "/pdf-to-png", text: "nav.pdf-to-png"},
      {href: "/pdf-to-ppt", text: "nav.pdf-to-ppt"},
    ],
  },
  {
    title: "Convert to PDF",
    items: [
      {href: "/word-to-pdf", text: "nav.word-to-pdf"},
      {href: "/excel-to-pdf", text: "nav.excel-to-pdf"},
      {href: "/jpg-to-pdf", text: "nav.jpg-to-pdf"},
      {href: "/html-to-pdf", text: "nav.html-to-pdf"},
    ],
  },
  {
    title: "Others",
    items: itemsEdit,
  },
];

const desktopMenuItems = [
  {text: "nav.all-tools", items: headerItems},
  // { text: 'nav.edit', items: itemsEdit },
  ...itemsOther,
];

const mobileMenuItems = {
  less: itemsEdit,
  more: [
    headerItems,
    {items: itemsOther},
  ],
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const {asPath} = useRouter()
  // Главная определяется по пути без query: на /?tool=sign|form это та же
  // главная, иначе пропадала бы «How to» и ломалась подсветка/aria-current.
  const itemsRight = asPath.split('?')[0] === '/' ? itemsOtherRightMain : itemsOtherRight

  const handleToggle = useCallback(() => {
    setOpen((open) => !open);
  }, []);

  return (
    <header className="header">
      <Container className="header__container">
        <div className="header__left">
          <HeaderLogo/>
          <HeaderMenu menuItems={desktopMenuItems} items={headerItems}/>
        </div>
        <div className="header__right">
          <HeaderMenu menuItems={itemsRight} items={itemsRight}/>
          <HeaderLangBox/>
          <HeaderMobileButton open={open} handleToggle={handleToggle}/>
          {/* <HeaderUser /> */}
        </div>
      </Container>
      <HeaderMobile
        open={open}
        setOpen={setOpen}
        handleToggle={handleToggle}
      />
    </header>
  );
};

const HeaderLogo = () => {
  const {t} = useTranslation("common");
  const {asPath, basePath} = useRouter();

  const handleClick = useCallback(() => {
    sendEvent({
      category: "header",
      action: "logo-click",
    });
  }, []);

  const img = (
    <img
      src={`${basePath}/images/logo.svg`}
      width="175"
      height="26"
      alt={t("header.logo")}
    />
  );

  if (asPath.split('?')[0] === "/") {
    return (
      <Link href="/">
        <a className="header__logo" onClick={handleClick} aria-current="page">
          {img}
        </a>
      </Link>
    );
  }

  return (
    <Link href="/">
      <a className="header__logo" onClick={handleClick}>
        {img}
      </a>
    </Link>
  );
};

const HeaderMenu = (menuItems, items) => {
  const {t} = useTranslation("common");
  const {asPath} = useRouter();

  return (
    <nav className="header__menu">
      <ul className="header__menu__list" role="menubar">
        {Array.isArray(menuItems.menuItems) && menuItems.menuItems.map((item, i) => (
          !item.disabled && (
            <HeaderMenuItem
              key={i}
              text={t(item.text)}
              href={item.href}
              active={item.href === asPath}
              locale={Array.isArray(items) && items.map(item => item.href).includes(item.href) ?
                false :
                undefined}
              item={item}
              i={i}
            />
          )
        ))}
      </ul>
    </nav>
  );
};

const HeaderMenuItem = ({text, href, active, locale, item, i}) => {
  const {t} = useTranslation("common");
  const {asPath} = useRouter();
  const [isShowSubmenu, setIsShowSubmenu] = useState(false);

  const handleClick = useCallback(() => {
    sendEvent({
      category: "header",
      action: "menu-link-click",
      label: href,
    });
  }, [href]);

  const handleToggleSubmenu = useCallback((nextState) => {
    setIsShowSubmenu(nextState);
  }, []);

  if (item.items) {
    return (
      <Dropdown
        itemSelector="a.header__submenu__link"
        show={isShowSubmenu}
        onToggle={handleToggleSubmenu}
      >
        <li className="header__menu__item" role="none">
          <Dropdown.Toggle>
            {(props) => (
              <button
                className="header__menu__link"
                type="button"
                role="menuitem"
                {...props}
              >
                {text}
                <IconArrowDown aria-hidden="true"/>
              </button>
            )}
          </Dropdown.Toggle>
          <HeaderSubmenu>
            {item.items && item.items.map((submenuItem, j) => (
              !submenuItem.disabled && (
                submenuItem.title
                  ?
                  <div className="header__menu__item__block" key={j}>
                    <span className="header__menu__item__block__title">{submenuItem.title}</span>
                    {submenuItem.items.map((item, index) => (
                      <HeaderSubmenuItem
                        key={`${i}:${j}:${index}`}
                        text={t(item.text)}
                        href={item.href}
                        active={item.href === asPath}
                        setIsShowSubmenu={setIsShowSubmenu}
                      />
                    ))}
                  </div>
                  :
                  <HeaderSubmenuItem
                    key={`${i}:${j}`}
                    text={t(submenuItem.text)}
                    href={submenuItem.href}
                    active={submenuItem.href === asPath}
                    setIsShowSubmenu={setIsShowSubmenu}
                  />
              )
            ))}
          </HeaderSubmenu>
        </li>
      </Dropdown>
    );
  }

  return (
    <li
      className={classNames(
        "header__menu__item",
        active && "header__menu__item--active",
      )}
      role="none"
    >
      {active ?
        (
          <span
            className="header__menu__link"
            role="menuitem"
            aria-current="page"
            onClick={handleClick}
          >
          {text}
        </span>
        ) :
        (
          <Link href={href} locale={locale}>
            <a
              className="header__menu__link"
              role="menuitem"
              onClick={handleClick}
            >
              {text}
            </a>
          </Link>
        )}
    </li>
  );
};

const HeaderSubmenu = ({children}) => {
  const [props] = useDropdownMenu();

  return (
    <ul
      className={classNames(
        "header__submenu",
        Children.count(children) > 5 && "header__submenu--multi-column",
      )}
      role="menu"
      {...props}
    >
      {children}
    </ul>
  );
};

const HeaderSubmenuItem = ({text, href, active, setIsShowSubmenu}) => {
  const {asPath} = useRouter();
  const handleClick = useCallback(() => {
    sendEvent({
      category: "click-to-open-pages",
      action: hrefToEventAction(href),
      label: "header",
    });
    active && setIsShowSubmenu(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [href]);

  return (
    <li
      className={classNames(
        "header__submenu__item",
        active && "header__submenu__item--active",
      )}
      role="none"
    >
      <Link href={href}>
        <a
          className={classNames(
            "header__submenu__link",
            asPath === href && "header__submenu__link__active",
          )}
          role="menuitem"
          onClick={handleClick}
        >
          {text}
        </a>
      </Link>
    </li>
  );
};

const HeaderLangBox = () => (
  <LangBox className="header__lang-box"/>
);

// eslint-disable-next-line no-unused-vars
const HeaderUser = () => {
  const {t} = useTranslation("common");
  const loggedIn = false;

  return (
    <div className={classNames(
      "header__user",
      loggedIn && "header__user--logged-in",
    )}>
      {loggedIn ?
        (
          <Link href="/account">
            <a className="header__user__link">
              {t("header.account")}
            </a>
          </Link>
        ) :
        (
          <Button
            as={Link}
            className="header__user__button"
            href="/login"
            size="sm"
            variant="primary"
          >
            {t("header.login")}
          </Button>
        )}
      <Link href={loggedIn ?
        "/account" :
        "/login"}>
        <a
          className="header__user__mobile-link"
          aria-label={loggedIn ?
            t("header.account") :
            t("header.login")}
        >
          <IconUser aria-hidden="true"/>
        </a>
      </Link>
    </div>
  );
};

const HeaderMobile = ({open, setOpen, handleToggle}) => {
  const handleClose = useCallback(() => {
    setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="header-mobile">
      <HeaderMobileMenu open={open} onToggle={handleToggle}/>
      {mobileMenuItems.more && open && (
        <HeaderMobileDropdownMenu onClose={handleClose}/>
      )}
    </div>
  );
};

const HeaderMobileButton = ({open, handleToggle}) => (
  <button
    className="header-mobile-button"
    type="button"
    onClick={handleToggle}
    aria-expanded={open}
  >
    <IconClose
      className="header-mobile-button__close"
      aria-hidden="true"
    />
  </button>
);

const HeaderMobileMenu = ({open, onToggle}) => {
  const {t} = useTranslation("common");
  const {asPath} = useRouter();

  return (
    <nav className="header-mobile__menu">
      <Container className="header-mobile__menu__container">
        <ul className="header-mobile__menu__list" role="menubar">
          {mobileMenuItems.less.map((item, i) => (
            !item.disabled && (
              <HeaderMobileMenuItem
                key={i}
                text={t(item.text)}
                href={item.href}
                active={item.href === asPath}
              />
            )
          ))}
          {mobileMenuItems.more && (
            <HeaderMobileMenuItem
              text={(
                <span>
                  {open ?
                    t("header.show_less") :
                    t("header.show_more")}
                </span>
              )}
              onClick={onToggle}
            />
          )}
        </ul>
      </Container>
    </nav>
  );
};

const HeaderMobileMenuItem = ({text, href, active, onClick}) => {
  const handleClick = useCallback((event) => {
    sendEvent({
      category: "click-to-open-pages",
      action: hrefToEventAction(href),
      label: "header",
    });

    onClick?.(event);
  }, [href, onClick]);

  return (
    <li
      className={classNames(
        "header-mobile__menu__item",
        active && "header-mobile__menu__item--active",
      )}
      role="none"
    >
      {href ?
        (
          <Link href={href}>
            <a
              className="header-mobile__menu__link"
              role="menuitem"
              onClick={handleClick}
            >
              {text}
            </a>
          </Link>
        ) :
        (
          <button
            className="header-mobile__menu__link"
            type="button"
            role="menuitem"
            onClick={onClick}
          >
            {text}
          </button>
        )}
    </li>
  );
};

const HeaderMobileDropdownMenu = ({onClose}) => {
  const {t} = useTranslation("common");
  const {asPath} = useRouter();
  const ref = useRef(null);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [onClose]);

  return (
    <nav className="header-mobile__dropdown-menu" ref={ref}>
      <Container className="header-mobile__dropdown-menu__container">
        <ul className="header-mobile__dropdown-menu__list" role="menubar">
          {mobileMenuItems.more.map((item, i) => (
            !item.disabled && (
              <li
                key={i}
                className="header-mobile__dropdown-menu__group"
                role="none"
              >
                {item.text && (
                  <div className="header-mobile__dropdown-menu__title">
                    {t(item.text)}
                  </div>
                )}
                <ul className="header-mobile__dropdown-menu__items" role="none">
                  {Array.isArray(item) && item.map((item, j) => (
                    !item.disabled && (
                      item.title
                        ?
                        <div className="header__menu__item__block" key={j}>
                          <span className="header__menu__item__block__title">{item.title}</span>
                          {item.items.map((element, index) => (
                            <HeaderMobileDropdownMenuItem
                              key={`${i}:${index}`}
                              text={t(element.text)}
                              href={element.href}
                              active={element.href === asPath}
                              onClose={onClose}
                              locale={itemsOther.map(item => item.href).includes(item.href) ?
                                false :
                                undefined}
                            />
                          ))}
                        </div>
                        :
                        <HeaderMobileDropdownMenuItem
                          key={`${i}:${j}`}
                          text={item.text}
                          href={item.href}
                          active={item.href === asPath}
                          onClose={onClose}
                          locale={itemsOther.map(item => item.href).includes(item.href) ?
                            false :
                            undefined}
                        />
                    )
                  ))}
                </ul>
              </li>
            )
          ))}
        </ul>
      </Container>
    </nav>
  );
};

const HeaderMobileDropdownMenuItem = ({text, href, active, onClose, locale}) => {
  const {t} = useTranslation("common");
  const {asPath} = useRouter();

  const handleClick = useCallback(() => {
    sendEvent({
      category: "click-to-open-pages",
      action: hrefToEventAction(href),
      label: "header",
    });
    onClose();
  }, [href, onClose]);

  return (
    <li
      className={classNames(
        "header-mobile__dropdown-menu__item",
        active && "header-mobile__dropdown-menu__item--active",
      )}
      role="none"
    >
      <Link href={href} locale={locale}>
        <a
          className={classNames(
            "header-mobile__dropdown-menu__link",
            asPath.includes(href) && "header-mobile__menu__link__active",
          )}
          role="menuitem"
          onClick={handleClick}
        >
          {t(text)}
        </a>
      </Link>
    </li>
  );
};

HeaderMenuItem.propTypes = {
  text: PropTypes.string.isRequired,
  href: PropTypes.string,
  active: PropTypes.bool,
  locale: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.number,
  ]),
  item: PropTypes.object.isRequired,
  i: PropTypes.number.isRequired,
};

HeaderSubmenu.propTypes = {
  children: PropTypes.node,
};

HeaderSubmenuItem.propTypes = {
  text: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  active: PropTypes.bool,
  setIsShowSubmenu: PropTypes.func,
};

HeaderMobile.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  handleToggle: PropTypes.func.isRequired,
};

HeaderMobileButton.propTypes = {
  open: PropTypes.bool.isRequired,
  handleToggle: PropTypes.func.isRequired,
};

HeaderMobileMenu.propTypes = {
  open: PropTypes.bool,
  onToggle: PropTypes.func,
};

HeaderMobileMenuItem.propTypes = {
  text: PropTypes.node.isRequired,
  href: PropTypes.string,
  active: PropTypes.bool,
  onClick: PropTypes.func,
};

HeaderMobileDropdownMenu.propTypes = {
  onClose: PropTypes.func.isRequired,
};

HeaderMobileDropdownMenuItem.propTypes = {
  text: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  active: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  locale: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.number,
  ]),
};

export default Header;
